import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { compose } from 'redux';
import WebFont from 'webfontloader';
import { customization, dialogs, globals, i18next, template } from '@yola/ws-sdk';
import segment from 'src/js/modules/analytics/segment';
import {
  SUBMIT_BUTTON_TRIGGER_ID,
  DIALOG_BACKGROUND_TRIGGER_ID,
} from '../../dialogs/constants/common.js';
import constants from '../constants';
import helpers from '../helpers';
import actions from '../actions';
import readOnlyModeDecorator from '../helpers/read-only-mode-decorator';
import DesignFontsDialog from '../components/design-fonts-dialog';

const {
  createMapById,
  prepareNodeElement,
  composeFontsAssetsPreviewUrl,
  getFontsConfig,
  setCurrentFont,
  getSelectorByGlobalId,
  sortPresets,
} = helpers;

const {
  constants: { events },
  trackers: { trackEvent },
} = segment;

const { GLOBAL_HEADER_CODE } = constants;
const DIALOG_CLASS_NAME = 'ws-design-fonts-dialog';

const getCaptions = () => ({
  breadcrumbs: [i18next.t('Design'), i18next.t('Fonts')],
  title: i18next.t('Select a font pair'),
  submitButton: i18next.t('Save'),
  cancelButton: i18next.t('Cancel'),
  description: i18next.t(
    'Switching fonts updates the appearance of all text on your site for consistent look and feel'
  ),
  searchPlaceholder: i18next.t('Search...'),
  emptyStateTitle: i18next.t('Sorry!'),
  emptyStateSubtitle: i18next.t('Nothing was found'),
});

const DesignFontsContainer = ({ onDialogMainAction, onDialogCancel, sourceId }) => {
  const fonts = useSelector(customization.selectors.getFontPairs);
  const customFontsTemplate = useSelector(customization.selectors.getFontPairTemplate);
  const currentFontPair = useSelector(customization.accessors.getCurrentFontPair);
  const fontsVariable = useSelector((state) =>
    template.selectors.getVariableValueByPath(state, customization.constants.FONTS_VARIABLE)
  );
  const initialFontPairId = currentFontPair?.id;

  const [activeFontPairId, setActiveFontPairId] = useState(initialFontPairId);
  const preloadedFonts = useRef({});

  const dispatch = useDispatch();

  const fontsMap = useMemo(() => createMapById(fonts), [fonts]);
  const sortedFonts = useMemo(
    () => sortPresets(fonts, currentFontPair?.id),
    [fonts, currentFontPair]
  );

  const captions = getCaptions();

  const changeFontPairs = () => dispatch(actions.changeFontPairs());

  const preLoadFonts = useCallback(
    (fontsId) => {
      const config = getFontsConfig(fontsId, fonts);

      if (!preloadedFonts.current[fontsId]) {
        WebFont.load(config);
      }

      preloadedFonts.current[fontsId] = true;
    },
    [fonts]
  );

  const applyFontPalette = (fontsId) => {
    const { element, paletteElement: fontsElement } = prepareNodeElement({
      elementVariable: fontsVariable,
      customTemplate: customFontsTemplate,
      currentPalette: fontsMap[fontsId],
      elementDataAttribute: customization.constants.FONTS_DATA_ATTRIBUTE,
    });
    if (element) {
      element.parentNode.replaceChild(fontsElement, element);
    }
  };

  const onClose = () => {
    dialogs.operations.hide();
  };

  const handleCancel = () => {
    if (initialFontPairId !== activeFontPairId) {
      applyFontPalette(initialFontPairId);
    }

    onDialogCancel({
      sourceId,
    });
    onClose();
  };

  const handleSubmit = (event) => {
    const isFontChanged = initialFontPairId !== activeFontPairId;

    if (isFontChanged) {
      globals.operations.changeGlobalContent(
        GLOBAL_HEADER_CODE,
        (global) => {
          const transformations = [];

          const font = fontsMap[activeFontPairId];
          const fontSelector = getSelectorByGlobalId(fontsVariable, GLOBAL_HEADER_CODE);
          const setCurrentFontCurry = (globalItem) =>
            setCurrentFont(globalItem, font, customFontsTemplate, fontSelector);
          transformations.push(setCurrentFontCurry);

          return compose(...transformations)(global);
        },
        false
      );

      changeFontPairs();
    }

    onDialogMainAction({
      triggerId: !event ? DIALOG_BACKGROUND_TRIGGER_ID : SUBMIT_BUTTON_TRIGGER_ID,
      sourceId,
      fontsAdjusted: isFontChanged,
      ...(isFontChanged && { fontPreset: activeFontPairId }),
    });
    onClose();
  };

  const onChangeFonts = (fontPairId) => {
    setActiveFontPairId(fontPairId);
    applyFontPalette(fontPairId);

    trackEvent(events.DESIGN_SETTINGS_FONT_PRESET_CLICKED, {
      fontPreset: fontPairId,
    });
  };

  const observer = useMemo(() => {
    const root = document.querySelector(`.${DIALOG_CLASS_NAME}`);
    return new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(({ isIntersecting, target }) => {
          if (isIntersecting) {
            preLoadFonts(target.id);
            obs.unobserve(target);
          }
        });
      },
      {
        root,
        rootMargin: '0px 0px 200px 0px',
      }
    );
  }, [preLoadFonts]);

  useEffect(() => () => observer.disconnect(), [observer]);

  return (
    <DesignFontsDialog
      captions={captions}
      fonts={composeFontsAssetsPreviewUrl(sortedFonts)}
      fontObserver={observer}
      activeFontId={activeFontPairId}
      setActiveFont={readOnlyModeDecorator(onChangeFonts)}
      onSubmit={handleSubmit}
      onCancel={handleCancel}
    />
  );
};

DesignFontsContainer.propTypes = {
  sourceId: PropTypes.string.isRequired,
  onDialogMainAction: PropTypes.func.isRequired,
  onDialogCancel: PropTypes.func.isRequired,
};

export default DesignFontsContainer;
